/**
 * Static seed data for local development.
 * Used as fallback when the backend API is unavailable (dev mode only).
 * Remove or gate behind import.meta.env.DEV — never ship to production.
 */
import type {
  ContextDomain,
  ContextChunkSummary,
  ContextConflict,
  DriftEvent,
  GapAnalysisResult,
  DomainScore,
  DetectedGap,
  GapRecommendation,
  ToolTemplate,
  FormattedContext,
  ContextChange,
} from '@/types/contextEngine'
import { ALL_DOMAINS } from '@/types/contextEngine'

// ─── Chunks per domain ──────────────────────────────────────────────────────

function chunk(
  id: string,
  domain: ContextDomain,
  text: string,
  opts: Partial<ContextChunkSummary> = {},
): ContextChunkSummary {
  return {
    chunkId: id,
    domain,
    entities: [],
    sourceType: 'document',
    sourceId: 'seed-doc-1',
    sourceTimestamp: '2026-03-20T10:00:00Z',
    confidence: 0.85,
    isContradicted: false,
    version: 1,
    status: 'active',
    createdAt: '2026-03-18T10:00:00Z',
    updatedAt: '2026-03-20T10:00:00Z',
    contentRef: `gs://dev-bucket/${id}.json`,
    text,
    ...opts,
  }
}

export const DEV_CHUNKS: Record<ContextDomain, ContextChunkSummary[]> = {
  mission: [
    chunk('m-1', 'mission', 'We are building the context layer for startups — a structured repository of everything a founder knows about their business, instantly accessible to any AI tool.', {
      subDomain: 'Vision',
      confidence: 0.95,
      entities: [{ name: 'contxtmd', type: 'product', salience: 0.9 }],
      sourceType: 'interview',
    }),
    chunk('m-2', 'mission', 'Our mission is to eliminate the "cold start" problem every time a founder uses a new AI tool. Context should travel with the founder, not be re-explained from scratch.', {
      subDomain: 'Mission Statement',
      confidence: 0.9,
    }),
    chunk('m-3', 'mission', 'Long-term, we want every AI interaction a founder has to be grounded in their full business context — from pitch deck to competitive intel to financials.', {
      subDomain: 'Long-term Vision',
      confidence: 0.75,
      sourceType: 'inference',
    }),
  ],

  target_market: [
    chunk('tm-1', 'target_market', 'Primary ICP: technical founders at seed-stage startups (1-10 employees) who use 3+ AI tools weekly. They feel the pain of repeating context across ChatGPT, Cursor, v0, and others.', {
      subDomain: 'ICP',
      confidence: 0.9,
      entities: [
        { name: 'Seed-stage founders', type: 'market_segment', salience: 0.95 },
        { name: 'ChatGPT', type: 'product', salience: 0.5 },
        { name: 'Cursor', type: 'product', salience: 0.5 },
      ],
    }),
    chunk('tm-2', 'target_market', 'TAM: ~500K startups globally using AI tools. SAM: ~80K US-based seed/Series A startups. SOM: ~5K early adopters in YC/TechStars network.', {
      subDomain: 'Market Size',
      confidence: 0.7,
      sourceType: 'document',
      entities: [{ name: '$2.4B', type: 'metric', salience: 0.8 }],
    }),
  ],

  business_model: [
    chunk('bm-1', 'business_model', 'Freemium SaaS: free tier (3 documents, 1 domain), Pro ($29/mo — unlimited docs, all domains, context packages), Team ($79/seat/mo — shared context, role-based access).', {
      subDomain: 'Pricing',
      confidence: 0.85,
      entities: [{ name: '$29/mo', type: 'metric', salience: 0.9 }],
    }),
    chunk('bm-2', 'business_model', 'Revenue model is usage-based at scale: context generation API calls billed per 1K tokens above the plan limit. This aligns our revenue with customer value.', {
      subDomain: 'Revenue Model',
      confidence: 0.7,
      sourceType: 'interview',
    }),
  ],

  product: [
    chunk('p-1', 'product', 'Core product: a context moat — AI-extracted, structured context engine from documents, interviews, and recordings. Three-layer storage: raw chunks, domain-classified metadata, source-of-truth views.', {
      subDomain: 'Architecture',
      confidence: 0.95,
      entities: [{ name: 'Context Engine', type: 'technology', salience: 0.95 }],
    }),
    chunk('p-2', 'product', 'Key differentiator: completeness radar. Visual representation of how much the AI knows about your startup across 12 domains. Gamifies context building — founders want to fill the gaps.', {
      subDomain: 'Features',
      confidence: 0.9,
      entities: [{ name: 'Completeness Radar', type: 'product', salience: 0.9 }],
    }),
    chunk('p-3', 'product', 'Context packages: scoped, formatted exports of your context tailored for specific tools. "Give Cursor my product context" generates a markdown file with relevant chunks.', {
      subDomain: 'Features',
      confidence: 0.85,
    }),
    chunk('p-4', 'product', 'Interview mode: conversational AI asks targeted questions to fill gaps. 5-minute sessions that extract structured data. Way more effective than asking founders to write docs.', {
      subDomain: 'Features',
      confidence: 0.8,
      sourceType: 'interview',
    }),
    chunk('p-5', 'product', 'Conflict detection: when two documents contradict each other (e.g. different pricing in pitch deck vs investor memo), the system flags it for resolution.', {
      subDomain: 'Features',
      confidence: 0.75,
      isContradicted: true,
      contradictedBy: 'p-1',
    }),
  ],

  go_to_market: [
    chunk('gtm-1', 'go_to_market', 'Launch strategy: dogfood internally for 2 weeks, then invite 20 YC founders for private beta. Gather feedback, iterate, then launch on Product Hunt + HN.', {
      subDomain: 'Launch Plan',
      confidence: 0.8,
      sourceType: 'interview',
    }),
  ],

  competition: [
    chunk('comp-1', 'competition', 'Direct competitors: Mem.ai (personal context engine, but no structured extraction), Notion AI (document Q&A, but no cross-tool context), CustomGPTs (per-tool, not portable).', {
      subDomain: 'Direct Competitors',
      confidence: 0.8,
      entities: [
        { name: 'Mem.ai', type: 'company', salience: 0.8 },
        { name: 'Notion AI', type: 'product', salience: 0.8 },
      ],
    }),
    chunk('comp-2', 'competition', 'Our moat: structured extraction + completeness scoring + cross-tool portability. Competitors do one of these, we do all three.', {
      subDomain: 'Competitive Advantage',
      confidence: 0.85,
      sourceType: 'interview',
    }),
  ],

  team: [
    chunk('t-1', 'team', 'Fritz (co-founder, CEO): Full-stack engineer, 8 years experience. Previously built internal tools at a fintech startup. Handles frontend and product.', {
      subDomain: 'Founders',
      confidence: 0.95,
      entities: [{ name: 'Fritz', type: 'person', salience: 0.95 }],
      sourceType: 'interview',
    }),
    chunk('t-2', 'team', 'Vidit Bhatia (co-founder, CTO): Backend/infrastructure engineer. Previously at Google Cloud. Handles backend, AI pipeline, and infrastructure.', {
      subDomain: 'Founders',
      confidence: 0.95,
      entities: [{ name: 'Vidit Bhatia', type: 'person', salience: 0.95 }],
      sourceType: 'interview',
    }),
  ],

  fundraising: [
    chunk('f-1', 'fundraising', 'Pre-seed: bootstrapped with $50K personal savings. Planning to raise a $500K pre-seed round after demonstrating product-market fit with 50+ beta users.', {
      subDomain: 'Funding Status',
      confidence: 0.7,
      entities: [{ name: '$500K', type: 'metric', salience: 0.9 }],
      sourceType: 'interview',
    }),
  ],

  validation: [
    chunk('v-1', 'validation', 'Talked to 15 founders. 12/15 said they spend 30+ minutes re-explaining their startup to each new AI tool. 8/15 said they would pay for a solution.', {
      subDomain: 'Customer Discovery',
      confidence: 0.85,
      entities: [{ name: '15 founders', type: 'metric', salience: 0.9 }],
      sourceType: 'interview',
    }),
  ],

  brand: [],

  legal: [
    chunk('l-1', 'legal', 'Delaware C-Corp incorporated January 2026. Standard SAFE notes for angel investors. IP assignment agreements signed by both founders.', {
      subDomain: 'Corporate Structure',
      confidence: 0.9,
      sourceType: 'document',
    }),
  ],

  operations: [
    chunk('o-1', 'operations', 'Tech stack: React + Firebase + GCP. CI/CD via GitHub Actions. Estimated monthly cloud cost at 100 users: ~$200/mo (Firestore + Cloud Functions + Gemini API).', {
      subDomain: 'Infrastructure',
      confidence: 0.8,
      entities: [
        { name: 'Firebase', type: 'technology', salience: 0.8 },
        { name: 'GCP', type: 'technology', salience: 0.8 },
      ],
    }),
  ],
}

// ─── Domain scores ──────────────────────────────────────────────────────────

const SCORE_MAP: Record<ContextDomain, number> = {
  mission: 82,
  target_market: 65,
  business_model: 55,
  product: 88,
  go_to_market: 30,
  competition: 60,
  team: 90,
  fundraising: 35,
  validation: 45,
  brand: 0,
  legal: 40,
  operations: 38,
}

const CONFIDENCE_MAP: Record<ContextDomain, number> = {
  mission: 0.87,
  target_market: 0.8,
  business_model: 0.78,
  product: 0.85,
  go_to_market: 0.8,
  competition: 0.83,
  team: 0.95,
  fundraising: 0.7,
  validation: 0.85,
  brand: 0,
  legal: 0.9,
  operations: 0.8,
}

export const DEV_DOMAIN_SCORES: DomainScore[] = ALL_DOMAINS.map((domain) => ({
  domain,
  score: SCORE_MAP[domain],
  activeChunks: DEV_CHUNKS[domain].length,
  avgConfidence: CONFIDENCE_MAP[domain],
  oldestChunkDays: Math.floor(Math.random() * 10),
  gaps: domain === 'brand' ? ['No brand identity defined'] :
        domain === 'go_to_market' ? ['Missing distribution channels', 'No launch timeline'] :
        domain === 'fundraising' ? ['Missing financial projections'] :
        [],
}))

// ─── Gaps ───────────────────────────────────────────────────────────────────

export const DEV_GAPS: DetectedGap[] = [
  {
    gapId: 'gap-1',
    uid: 'dev-user',
    domain: 'brand',
    severity: 0.9,
    description: 'No brand identity, voice guidelines, or visual language defined.',
    suggestedResolution: {
      method: 'ask_question',
      questions: [
        'What personality should your brand convey?',
        'Who are your brand role models?',
        'What tone do you use in customer communications?',
      ],
    },
    detectedAt: '2026-03-22T00:00:00Z',
    status: 'open',
  },
  {
    gapId: 'gap-2',
    uid: 'dev-user',
    domain: 'go_to_market',
    severity: 0.8,
    description: 'Missing distribution channel strategy — how will users discover the product?',
    suggestedResolution: {
      method: 'ask_question',
      questions: [
        'What channels will you use to reach early adopters?',
        'Do you have existing communities or networks to leverage?',
      ],
    },
    detectedAt: '2026-03-22T00:00:00Z',
    status: 'open',
  },
  {
    gapId: 'gap-3',
    uid: 'dev-user',
    domain: 'go_to_market',
    severity: 0.6,
    description: 'No concrete launch timeline with milestones.',
    suggestedResolution: { method: 'request_document', documentTypes: ['launch plan', 'roadmap'] },
    detectedAt: '2026-03-22T00:00:00Z',
    status: 'open',
  },
  {
    gapId: 'gap-4',
    uid: 'dev-user',
    domain: 'fundraising',
    severity: 0.7,
    description: 'No financial projections or unit economics model.',
    suggestedResolution: { method: 'request_document', documentTypes: ['financial model', 'projections spreadsheet'] },
    detectedAt: '2026-03-22T00:00:00Z',
    status: 'open',
  },
  {
    gapId: 'gap-5',
    uid: 'dev-user',
    domain: 'product',
    severity: 0.4,
    description: 'Technical architecture documentation is incomplete — missing data flow diagrams.',
    suggestedResolution: {
      method: 'infer_from_existing',
    },
    detectedAt: '2026-03-22T00:00:00Z',
    status: 'open',
  },
  {
    gapId: 'gap-6',
    uid: 'dev-user',
    domain: 'validation',
    severity: 0.5,
    description: 'Customer discovery interviews are qualitative only — no quantitative survey data.',
    suggestedResolution: {
      method: 'request_document',
      documentTypes: ['survey results', 'market research'],
    },
    detectedAt: '2026-03-22T00:00:00Z',
    status: 'open',
  },
]

// ─── Recommendations ────────────────────────────────────────────────────────

export const DEV_RECOMMENDATIONS: GapRecommendation[] = [
  {
    id: 'rec-1',
    type: 'start_interview',
    priority: 'critical',
    title: 'Define your brand identity',
    description: 'Answer 5 questions about brand voice, personality, and visual direction.',
    domain: 'brand',
    actionLabel: 'Start Interview',
    actionUrl: '/context/add?mode=interview&domain=brand',
  },
  {
    id: 'rec-2',
    type: 'fill_gap',
    priority: 'high',
    title: 'Map your distribution channels',
    description: 'Describe how users will discover and sign up for your product.',
    domain: 'go_to_market',
    actionLabel: 'Fill Gap',
    actionUrl: '/context/add?mode=interview&domain=go_to_market',
  },
  {
    id: 'rec-3',
    type: 'upload_document',
    priority: 'high',
    title: 'Upload financial projections',
    description: 'A spreadsheet or deck with revenue forecasts and unit economics.',
    domain: 'fundraising',
    actionLabel: 'Upload',
    actionUrl: '/context/add?mode=upload&domain=fundraising',
  },
]

// ─── Conflicts ─────────────────────────────────────────────────────────────

export const DEV_CONFLICTS: ContextConflict[] = [
  {
    conflictId: 'conflict-1',
    domain: 'business_model',
    chunkA: chunk('bm-1', 'business_model', 'Freemium SaaS: free tier (3 documents, 1 domain), Pro ($29/mo — unlimited docs, all domains, context packages), Team ($79/seat/mo — shared context, role-based access).', {
      subDomain: 'Pricing',
      confidence: 0.85,
      sourceType: 'interview',
      sourceId: 'interview-2026-03-15',
      sourceTimestamp: '2026-03-15T14:30:00Z',
    }),
    chunkB: chunk('bm-pricing-v2', 'business_model', 'Pricing: Free tier (5 documents, 3 domains), Pro ($19/mo — unlimited docs, all domains), Team ($49/seat/mo — shared context, admin controls, SSO).', {
      subDomain: 'Pricing',
      confidence: 0.8,
      sourceType: 'document',
      sourceId: 'pitch-deck-v3',
      sourceTimestamp: '2026-03-22T09:00:00Z',
    }),
    aiMergedText: 'Freemium SaaS: free tier (5 documents, 3 domains), Pro ($19/mo — unlimited docs, all domains, context packages), Team ($49/seat/mo — shared context, role-based access, admin controls, SSO). Note: pricing was updated in the March 2026 pitch deck revision, lowering Pro from $29 to $19 and Team from $79 to $49.',
    status: 'unresolved',
    detectedAt: '2026-03-22T09:05:00Z',
  },
  {
    conflictId: 'conflict-2',
    domain: 'target_market',
    chunkA: chunk('tm-2', 'target_market', 'TAM: ~500K startups globally using AI tools. SAM: ~80K US-based seed/Series A startups. SOM: ~5K early adopters in YC/TechStars network.', {
      subDomain: 'Market Size',
      confidence: 0.7,
      sourceType: 'document',
      sourceId: 'market-research-v1',
      sourceTimestamp: '2026-03-10T10:00:00Z',
    }),
    chunkB: chunk('tm-market-v2', 'target_market', 'TAM: ~1.2M startups globally using AI tools (revised estimate including non-US markets). SAM: ~200K seed/Series A startups in US + EU. SOM: ~15K early adopters across YC, TechStars, and European accelerator networks.', {
      subDomain: 'Market Size',
      confidence: 0.75,
      sourceType: 'document',
      sourceId: 'investor-memo-q1',
      sourceTimestamp: '2026-03-20T16:00:00Z',
    }),
    aiMergedText: 'TAM: ~1.2M startups globally using AI tools (revised Q1 2026 estimate). SAM: ~200K seed/Series A startups in US + EU markets. SOM: ~15K early adopters across YC, TechStars, and European accelerator networks. Previous estimate was lower (~500K TAM) based on US-centric data.',
    status: 'unresolved',
    detectedAt: '2026-03-20T16:10:00Z',
  },
  {
    conflictId: 'conflict-3',
    domain: 'product',
    chunkA: DEV_CHUNKS.product[0], // p-1: three-layer storage architecture
    chunkB: chunk('p-arch-v2', 'product', 'Core architecture: two-layer storage — metadata layer (Firestore) with embedded vectors, and source-of-truth aggregation views. GCS blob storage was considered but deferred for simplicity.', {
      subDomain: 'Architecture',
      confidence: 0.65,
      sourceType: 'document',
      sourceId: 'early-design-doc',
      sourceTimestamp: '2026-02-28T11:00:00Z',
    }),
    status: 'unresolved',
    detectedAt: '2026-03-18T12:00:00Z',
  },
]

// ─── Drift events (for toast simulation) ───────────────────────────────────

export const DEV_DRIFT_EVENTS: DriftEvent[] = [
  {
    domain: 'business_model',
    eventType: 'CONFLICT_DETECTED',
    description: 'Your pricing model may have changed — new pitch deck contradicts interview data',
    conflictId: 'conflict-1',
    timestamp: '2026-03-24T10:30:00Z',
  },
  {
    domain: 'target_market',
    eventType: 'CHUNKS_INDEXED',
    description: 'Market size estimates updated from investor memo',
    chunkId: 'tm-market-v2',
    timestamp: '2026-03-24T10:25:00Z',
  },
  {
    domain: 'product',
    eventType: 'CONFLICT_DETECTED',
    description: 'Architecture description conflict — two-layer vs three-layer storage',
    conflictId: 'conflict-3',
    timestamp: '2026-03-24T10:20:00Z',
  },
]

// ─── Full gap analysis result ───────────────────────────────────────────────

export const DEV_GAP_ANALYSIS: GapAnalysisResult = {
  uid: 'dev-user',
  analyzedAt: '2026-03-24T10:00:00Z',
  overallScore: 52,
  domainScores: DEV_DOMAIN_SCORES,
  gaps: DEV_GAPS,
  recommendations: DEV_RECOMMENDATIONS,
}

// ─── Templates ──────────────────────────────────────────────────────────────

export const DEV_TEMPLATES: ToolTemplate[] = [
  {
    templateId: 'tpl-cursor',
    toolName: 'Cursor',
    category: 'code',
    enabled: true,
    selection: {
      strategy: 'deep',
      includeDomains: ['product', 'operations', 'team', 'business_model'],
      excludeDomains: [],
      maxTokens: 8000,
    },
  },
  {
    templateId: 'tpl-summary',
    toolName: 'Executive Summary',
    category: 'general',
    enabled: true,
    selection: {
      strategy: 'balanced',
      includeDomains: ['mission', 'target_market', 'business_model', 'product', 'team', 'fundraising', 'validation'],
      excludeDomains: [],
      maxTokens: 4000,
    },
  },
  {
    templateId: 'tpl-pitch',
    toolName: 'Pitch Deck',
    category: 'presentation',
    enabled: true,
    selection: {
      strategy: 'balanced',
      includeDomains: ['mission', 'target_market', 'business_model', 'product', 'competition', 'team', 'fundraising', 'validation'],
      excludeDomains: [],
      maxTokens: 6000,
    },
  },
  {
    templateId: 'tpl-product-spec',
    toolName: 'Product Spec',
    category: 'documentation',
    enabled: true,
    selection: {
      strategy: 'deep',
      includeDomains: ['product', 'target_market', 'competition', 'operations'],
      excludeDomains: [],
      maxTokens: 10000,
    },
  },
  {
    templateId: 'tpl-investor-memo',
    toolName: 'Investor Memo',
    category: 'copy',
    enabled: true,
    selection: {
      strategy: 'balanced',
      includeDomains: ['mission', 'target_market', 'business_model', 'fundraising', 'validation', 'team'],
      excludeDomains: ['legal', 'operations'],
      maxTokens: 5000,
    },
  },
  {
    templateId: 'tpl-research',
    toolName: 'Market Research Brief',
    category: 'research',
    enabled: false,
    selection: {
      strategy: 'semantic_only',
      includeDomains: ['target_market', 'competition', 'validation'],
      excludeDomains: [],
      maxTokens: 4000,
    },
  },
]

// ─── Generated package sample ───────────────────────────────────────────────

export const DEV_GENERATED_PACKAGE: FormattedContext = {
  content: `# Project Context — contxtmd

## Vision & Mission
We are building the context layer for startups — a structured repository of everything a founder knows about their business, instantly accessible to any AI tool. Our mission is to eliminate the "cold start" problem every time a founder uses a new AI tool.

## Product
Core product: a context moat — AI-extracted, structured context engine from documents, interviews, and recordings. Three-layer storage: raw chunks, domain-classified metadata, source-of-truth views.

Key differentiator: completeness radar. Visual representation of how much the AI knows about your startup across 12 domains.

Context packages: scoped, formatted exports of your context tailored for specific tools.

## Target Market
Primary ICP: technical founders at seed-stage startups (1-10 employees) who use 3+ AI tools weekly.
TAM: ~500K startups globally using AI tools. SAM: ~80K US-based seed/Series A startups. SOM: ~5K early adopters.

## Business Model
Freemium SaaS: Free tier (3 docs, 1 domain), Pro ($29/mo — unlimited), Team ($79/seat/mo — shared context).

## Team
- Fritz (co-founder, CEO): Full-stack engineer, 8 years experience. Handles frontend and product.
- Vidit Bhatia (co-founder, CTO): Backend/infrastructure engineer. Previously at Google Cloud.

## Tech Stack
React + Firebase + GCP. CI/CD via GitHub Actions. Estimated monthly cloud cost at 100 users: ~$200/mo.
`,
  tokenCount: 1847,
  metadata: {
    templateId: 'tpl-cursor',
    strategy: 'deep',
    chunksUsed: 14,
    domainsIncluded: ['mission', 'product', 'target_market', 'business_model', 'team', 'operations'],
    warnings: [],
    generatedAt: '2026-03-24T11:00:00Z',
  },
}

// ─── History changes ────────────────────────────────────────────────────────

export const DEV_HISTORY: ContextChange[] = [
  {
    changeId: 'chg-1',
    domain: 'business_model',
    chunkId: 'bm-pricing-v2',
    changeType: 'updated',
    oldText: 'Freemium SaaS: free tier (3 documents, 1 domain), Pro ($29/mo — unlimited docs, all domains, context packages), Team ($79/seat/mo — shared context, role-based access).',
    newText: 'Pricing: Free tier (5 documents, 3 domains), Pro ($19/mo — unlimited docs, all domains), Team ($49/seat/mo — shared context, admin controls, SSO).',
    sourceType: 'document',
    sourceId: 'pitch-deck-v3',
    timestamp: '2026-03-24T09:00:00Z',
  },
  {
    changeId: 'chg-2',
    domain: 'target_market',
    chunkId: 'tm-market-v2',
    changeType: 'created',
    newText: 'TAM: ~1.2M startups globally using AI tools (revised estimate including non-US markets). SAM: ~200K seed/Series A startups in US + EU. SOM: ~15K early adopters across YC, TechStars, and European accelerator networks.',
    sourceType: 'document',
    sourceId: 'investor-memo-q1',
    timestamp: '2026-03-23T16:00:00Z',
  },
  {
    changeId: 'chg-3',
    domain: 'product',
    chunkId: 'p-3',
    changeType: 'updated',
    oldText: 'Context packages: formatted exports of your context for external tools.',
    newText: 'Context packages: scoped, formatted exports of your context tailored for specific tools. "Give Cursor my product context" generates a markdown file with relevant chunks.',
    sourceType: 'interview',
    sourceId: 'interview-2026-03-22',
    timestamp: '2026-03-23T14:30:00Z',
  },
  {
    changeId: 'chg-4',
    domain: 'team',
    chunkId: 't-2',
    changeType: 'updated',
    oldText: 'Vidit Bhatia (co-founder, CTO): Backend engineer.',
    newText: 'Vidit Bhatia (co-founder, CTO): Backend/infrastructure engineer. Previously at Google Cloud. Handles backend, AI pipeline, and infrastructure.',
    sourceType: 'interview',
    sourceId: 'interview-2026-03-21',
    timestamp: '2026-03-22T10:15:00Z',
  },
  {
    changeId: 'chg-5',
    domain: 'competition',
    chunkId: 'comp-2',
    changeType: 'created',
    newText: 'Our moat: structured extraction + completeness scoring + cross-tool portability. Competitors do one of these, we do all three.',
    sourceType: 'interview',
    sourceId: 'interview-2026-03-21',
    timestamp: '2026-03-22T10:10:00Z',
  },
  {
    changeId: 'chg-6',
    domain: 'fundraising',
    chunkId: 'f-1',
    changeType: 'updated',
    oldText: 'Planning to raise a seed round after MVP launch.',
    newText: 'Pre-seed: bootstrapped with $50K personal savings. Planning to raise a $500K pre-seed round after demonstrating product-market fit with 50+ beta users.',
    sourceType: 'interview',
    sourceId: 'interview-2026-03-20',
    timestamp: '2026-03-21T11:00:00Z',
  },
  {
    changeId: 'chg-7',
    domain: 'mission',
    chunkId: 'm-1',
    changeType: 'reverted',
    oldText: 'We are building an AI assistant for startup founders.',
    newText: 'We are building the context layer for startups — a structured repository of everything a founder knows about their business, instantly accessible to any AI tool.',
    sourceType: 'manual_edit',
    sourceId: 'revert-by-fritz',
    timestamp: '2026-03-20T16:00:00Z',
  },
  {
    changeId: 'chg-8',
    domain: 'legal',
    chunkId: 'l-1',
    changeType: 'created',
    newText: 'Delaware C-Corp incorporated January 2026. Standard SAFE notes for angel investors. IP assignment agreements signed by both founders.',
    sourceType: 'document',
    sourceId: 'incorporation-docs',
    timestamp: '2026-03-19T09:00:00Z',
  },
  {
    changeId: 'chg-9',
    domain: 'validation',
    chunkId: 'v-1',
    changeType: 'created',
    newText: 'Talked to 15 founders. 12/15 said they spend 30+ minutes re-explaining their startup to each new AI tool. 8/15 said they would pay for a solution.',
    sourceType: 'interview',
    sourceId: 'interview-2026-03-18',
    timestamp: '2026-03-18T15:30:00Z',
  },
  {
    changeId: 'chg-10',
    domain: 'product',
    chunkId: 'p-arch-old',
    changeType: 'archived',
    oldText: 'Core architecture: two-layer storage — metadata layer (Firestore) with embedded vectors, and source-of-truth aggregation views. GCS blob storage was considered but deferred for simplicity.',
    sourceType: 'document',
    sourceId: 'early-design-doc',
    timestamp: '2026-03-18T12:00:00Z',
  },
]
